import React, { useEffect } from 'react'

import { sortBy, groupBy, uniq } from 'lodash'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSkeleton
} from 'prosci-ui'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'prosci-ui'
import { ChevronRightIcon, ArrowLeft } from '../../utils/icons'
import {
  activeNavTreeIds,
} from '../../utils'
import { getSideNavItemsGroups } from '../../features/programs/utils'
import {
  useAppCopy,
  useHubAppCopy,
} from '../../hooks'
import { APP_MAPPING_BY_SLUG, HubSlug } from "../../config";
import { ViewTransitionLink } from '../ui/ViewTransitionLink'

interface NavItem {
  id: string;
  name: string;
  path: string;
  children: NavItem[];
}

interface RecursiveNavProps {
  items: NavItem[];
  currentPath: string;
  expandedNavItems: string[];
  setExpandedNavItems: (items: string[]) => void;
}

const RecursiveNav: React.FC<RecursiveNavProps> = ({ items, currentPath, expandedNavItems, setExpandedNavItems }) => {

  const handleToggle = (id: string) => {
    const newExpanded = expandedNavItems.includes(id)
      ? expandedNavItems.filter(i => i !== id)
      : [...expandedNavItems, id];
    setExpandedNavItems(newExpanded);
  }

  return (
    <SidebarMenu>
      {items.map((item) => (
        <SidebarMenuItem key={item.id}>
          {item.children && item.children.length > 0 ? (
            <Collapsible open={expandedNavItems.includes(item.id)}>
              <div className={`flex items-center gap-1 rounded-md transition-colors ${currentPath === item.path ? 'bg-primary-50' : ''}`}>
                <SidebarMenuButton
                  asChild
                  className="flex-1 !bg-transparent hover:!bg-transparent data-[active=true]:!bg-transparent"
                >
                  <ViewTransitionLink to={item.path}>
                    {item.name}
                  </ViewTransitionLink>
                </SidebarMenuButton>
                <CollapsibleTrigger asChild>
                  <button 
                    className="px-2 py-2 hover:opacity-70 transition-opacity flex items-center justify-center"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleToggle(item.id);
                    }}
                  >
                    <ChevronRightIcon className={`h-4 w-4 transition-transform duration-200 ${expandedNavItems.includes(item.id) ? 'rotate-90' : ''}`} />
                  </button>
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent>
                 <div className="pl-4">
                    <RecursiveNav items={item.children} currentPath={currentPath} expandedNavItems={expandedNavItems} setExpandedNavItems={setExpandedNavItems} />
                 </div>
              </CollapsibleContent>
            </Collapsible>
          ) : (
            <SidebarMenuButton asChild isActive={currentPath === item.path}>
              <ViewTransitionLink to={item.path}>{item.name}</ViewTransitionLink>
            </SidebarMenuButton>
          )}
        </SidebarMenuItem>
      ))}
    </SidebarMenu>
  );
};

interface SideNavProps {
  activeTopLevelNav?: string
  currentPath?: string
  expandedNavItems: string[]
  expandedNavItemProgramName?: string
  expandedSideNav: boolean
  programs: any[]
  program?: any
  setExpandedNavItems: (items: string[]) => void
  setExpandedSideNav: (expanded: boolean) => void
  activeHubSlug?: HubSlug
  missionControlOffset?: number // kept for backwards compatibility, not used internally
}

function SideNav({
  activeTopLevelNav,
  currentPath,
  programs,
  expandedNavItems,
  expandedNavItemProgramName,
  expandedSideNav,
  setExpandedNavItems,
  setExpandedSideNav,
  program,
  activeHubSlug,
}: SideNavProps) {
  const appCopy = useAppCopy()
  const hubAppCopy = useHubAppCopy()

  // Check if we have all required props for full rendering
  // Note: When viewing a specific program, programs array can be empty but we still have the program prop
  const hasRequiredProps = !!(activeHubSlug && (program || (programs && programs.length > 0)) && expandedNavItems && setExpandedNavItems && setExpandedSideNav)

  const appMapping = activeHubSlug ? APP_MAPPING_BY_SLUG[activeHubSlug] : {};

  // Compute navItemGroups only when we have required props, otherwise use empty array
  const navItemGroups: any[] = hasRequiredProps
    ? getSideNavItemsGroups(programs, program, appCopy, hubAppCopy, activeHubSlug)
    : []

  // set and maintain the side nav expanded items based on active program
  // All hooks must be called unconditionally before any early returns
  useEffect(() => {
    if (!hasRequiredProps) return

    const activeNavItems = activeNavTreeIds(currentPath, activeTopLevelNav, navItemGroups)

    // re-expand side nav if visiting from a non-nav page
    if (!expandedNavItemProgramName && activeNavItems.length > 0)
      setExpandedSideNav(true)

    // Set expanded items to only the active nav items on navigation
    // This allows manual toggling to work, and resets on each navigation
    if (setExpandedNavItems && activeNavItems.length > 0) {
      setExpandedNavItems(activeNavItems)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- navItemGroups is derived from program/programs which are stable, including it causes infinite loops
  }, [hasRequiredProps, expandedNavItemProgramName, currentPath, activeTopLevelNav, program?.slug])

  useEffect(() => {
    if (!hasRequiredProps) return

    const activeNavElement = document.querySelector('.isCurrentPath')
    if (!activeNavElement) return
    activeNavElement.scrollIntoView({ behavior: 'smooth' })
  }, [hasRequiredProps, expandedNavItems])

  // Now we can safely do early returns after all hooks have been called
  if (!hasRequiredProps) {
    return (
      <Sidebar
        collapsible="none"
        className="w-[255px] border-r border-gray-200 h-full"
      >
        <SidebarContent>
          <SidebarGroup>
            <SidebarMenu>
              {Array.from({ length: 8 }).map((_, index) => (
                <SidebarMenuItem key={index}>
                  <SidebarMenuSkeleton showIcon />
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
    )
  }

  if (activeHubSlug === 'knowledge' && !program) {
    const sortedPrograms = sortBy(programs, (p) => p.programName?.toLowerCase())
    const programsByType = groupBy(sortedPrograms, 'contentType')

    return (
      <Sidebar
        collapsible="none"
        className="w-[255px] border-r border-gray-200 h-full"
      >
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wide text-secondary">{hubAppCopy('programMaterials')}</SidebarGroupLabel>
            <SidebarMenu>
              {programsByType['programMaterials']?.map((program: any, index: number) => (
                <SidebarMenuItem key={`pm-${index}`}>
                  <SidebarMenuButton asChild>
                    <ViewTransitionLink to={`/${activeHubSlug}/${(appMapping as any).route}/${program.slug}`}>{program.programName}</ViewTransitionLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wide text-secondary">{hubAppCopy('applicationResources')}</SidebarGroupLabel>
            <SidebarMenu>
              {programsByType['applicationResources']?.map((program: any, index: number) => (
                <SidebarMenuItem key={`ar-${index}`}>
                  <SidebarMenuButton asChild>
                    <ViewTransitionLink to={`/${activeHubSlug}/${(appMapping as any).route}/${program.slug}`}>{program.programName}</ViewTransitionLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
    )
  }

  const [allPrograms, ...programNavs] = navItemGroups;

  return (
    <Sidebar
      collapsible="none"
      className="w-[255px] border-r border-gray-200 h-full"
    >
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu className="py-3 text-tertiary">
            {program ? (
              <SidebarMenuItem className="border-b border-border border-solid pb-3 mb-1">
                <SidebarMenuButton asChild>
                  <ViewTransitionLink to={(allPrograms as any).topLevelNavPath}><ArrowLeft className="w-4 h-4" /> {(allPrograms as any).topLevelNav}</ViewTransitionLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ) : null}

            {programNavs.map((group: any) => (
              <SidebarMenuItem key={group.topLevelNav}>
                <SidebarMenuButton asChild>
                  <ViewTransitionLink to={group.topLevelNavPath}>
                    <span className="font-semibold">{group.topLevelNav}</span>
                  </ViewTransitionLink>
                </SidebarMenuButton>
                {group.navItems && group.navItems.length > 0 && (
                  <RecursiveNav items={group.navItems} currentPath={currentPath || ''} expandedNavItems={expandedNavItems || []} setExpandedNavItems={setExpandedNavItems} />
                )}
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}


export default SideNav
